import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import useAnimate from '../ui/Motion';

const EASE = [0.16, 1, 0.3, 1];

/* Project card, photo and detail modal, shared by the About page preview
   and the Portfolio page's residential and commercial sections. */

/** Renders a titled placeholder until the photo exists in
    public/media/projects/, so a missing image never breaks the grid. */
export function ProjectPhoto({ project }) {
    const [failed, setFailed] = useState(!project.photo);

    if (failed)
        return <div className="project-placeholder" aria-hidden="true"><span>{project.name}</span></div>;

    return (
        <img
            className="project-photo"
            src={project.photo}
            alt={project.name}
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
        />
    );
}

export function ProjectMeta({ project }) {
    return (
        <dl className="modal__meta">
            {project.units && (
                <div>
                    <dt className="project-meta-label">Units</dt>
                    <dd>{project.units}</dd>
                </div>
            )}
            {project.gc && (
                <div>
                    <dt className="project-meta-label">General Contractor</dt>
                    <dd>{project.gc}</dd>
                </div>
            )}
            {project.developer && (
                <div>
                    <dt className="project-meta-label">Developer</dt>
                    <dd>{project.developer}</dd>
                </div>
            )}
        </dl>
    );
}

/**
 * The shared modal shell. Locks the page behind it, closes on Escape and
 * on a click outside, and restores the scroll lock on unmount so a route
 * change while a dialog is open cannot leave the page frozen.
 */
export function Modal({ title, subtitle, closeLabel, labelledBy, onClose, children }) {
    const reduced = !useAnimate();

    useEffect(() => {
        const onKey = event => { if (event.key === 'Escape') onClose(); };

        document.addEventListener('keydown', onKey);
        document.body.classList.add('is-locked');

        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.classList.remove('is-locked');
        };
    }, [onClose]);

    return (
        <motion.div
            className="modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy}
            onClick={onClose}
            initial={reduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25, ease: EASE }}
        >
            <motion.div
                className="modal"
                onClick={event => event.stopPropagation()}
                initial={reduced ? false : { opacity: 0, y: 24, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.45, ease: EASE }}
            >
                <div className="modal__header">
                    <div>
                        <h2 id={labelledBy} className="modal__title">{title}</h2>
                        {subtitle && <p className="leader-role">{subtitle}</p>}
                    </div>

                    <button
                        type="button"
                        className="modal__close"
                        onClick={onClose}
                        aria-label={closeLabel}
                    >
                        &times;
                    </button>
                </div>

                {children}
            </motion.div>
        </motion.div>
    );
}

export function ProjectModal({ project, onClose }) {
    return (
        <Modal
            title={project.name}
            subtitle={project.location}
            closeLabel="Close project details"
            labelledBy="projectModalName"
            onClose={onClose}
        >
            <ProjectPhoto project={project} />

            <div className="modal__body">
                {project.description && <p>{project.description}</p>}
                <ProjectMeta project={project} />
            </div>
        </Modal>
    );
}

/** A single tile in the grid. */
export function ProjectCard({ project, onSelect }) {
    return (
        <button
            type="button"
            className="project-card"
            onClick={() => onSelect(project)}
            aria-label={`View details for ${project.name}`}
        >
            <ProjectPhoto project={project} />

            <div className="project-card__body">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
                    <h3 className="project-card__name">{project.name}</h3>
                    {project.status && <span className="about-chip about-chip--status">{project.status}</span>}
                </div>

                <p className="project-card-meta">
                    {project.location || (project.units ? `${project.units} units` : project.gc)}
                </p>

                {project.location && project.units && (
                    <p className="project-card-meta">{project.units} units</p>
                )}
            </div>
        </button>
    );
}

export function ProjectGrid({ items, onSelect, cols = 4 }) {
    const reduced = !useAnimate();

    return (
        <ul className="cards" style={{ '--cols': cols }}>
            {items.map((item, i) => (
                <motion.li
                    key={item.id}
                    initial={reduced ? false : { opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.65, delay: (i % cols) * 0.06, ease: EASE }}
                >
                    <ProjectCard project={item} onSelect={onSelect} />
                </motion.li>
            ))}
        </ul>
    );
}
